/**
 * Landing page micro-animations (CSS + Framer Motion).
 * Each component is a self-contained "fake GIF" looping animation
 * designed to evoke the actual product experience without screen recording.
 */
import React from 'react';
import { motion } from 'framer-motion';

const ACCENT = '#E66C2C';

/* ============================================================
   1) 3D MODEL ROTATION — real screen capture GIF of an STL rotating
   ============================================================ */
export function Animation3DRotation() {
  return (
    <div className="relative w-full mx-auto flex items-center justify-center" style={{ maxWidth: '500px' }} data-testid="anim-3d-rotation">
      <img
        src="/landing/hero_stl_rotation.gif"
        alt="Modèle STL en rotation 3D — visualisation Hamac"
        className="w-full h-auto object-contain"
        loading="eager"
      />
      <div className="absolute bottom-2 left-2 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
        STL · 3D
      </div>
    </div>
  );
}

/* ============================================================
   2) CURSOR DOWNLOAD (or real screen recording) — replaced by GIF when available
   ============================================================ */
export function AnimationCursorDownload() {
  return (
    <div className="w-full border border-zinc-200 overflow-hidden bg-white" data-testid="anim-cursor-download">
      <img
        src="/landing/visualisation_stl.gif"
        alt="Accès aux moulages STL sur la plateforme Hamac"
        className="w-full h-auto block"
        loading="lazy"
      />
      <div className="flex items-center justify-between px-3 py-2 border-t border-zinc-100 bg-zinc-50">
        <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Plateforme · Démo</span>
        <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-400">visualisation STL</span>
      </div>
    </div>
  );
}

/* ============================================================
   3) ORDER TIMELINE — 5 dots, progress line filling left to right
   ============================================================ */
const TIMELINE_STEPS = ['Saisie', 'Val. 3D', 'Fabrication', 'Expédiée', 'Livrée'];

export function AnimationOrderTimeline() {
  return (
    <div className="relative w-full max-w-md py-6" data-testid="anim-order-timeline">
      {/* Base line */}
      <div className="absolute left-3 right-3 top-1/2 h-px bg-zinc-200" />
      {/* Animated progress line */}
      <motion.div
        className="absolute left-3 top-1/2 h-px origin-left"
        style={{ backgroundColor: ACCENT, width: 'calc(100% - 24px)' }}
        initial={{ scaleX: 0 }}
        animate={{ scaleX: [0, 0.25, 0.5, 0.75, 1, 1] }}
        transition={{ duration: 6, repeat: Infinity, times: [0, 0.2, 0.4, 0.6, 0.85, 1], ease: 'easeInOut' }}
      />
      {/* Dots */}
      <div className="relative flex justify-between items-center">
        {TIMELINE_STEPS.map((label, i) => (
          <div key={label} className="flex flex-col items-center gap-2">
            <motion.div
              className="w-4 h-4 rounded-full border-2 border-zinc-200 bg-white relative z-10"
              animate={{
                backgroundColor: ['#fff', '#fff', ACCENT, ACCENT],
                borderColor: ['#e4e4e7', '#e4e4e7', ACCENT, ACCENT],
                scale: [1, 1, 1.25, 1],
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                times: [0, (i * 0.18) + 0.05, (i * 0.18) + 0.18, 1],
                ease: 'easeInOut',
              }}
            />
            <span className="font-mono text-[8px] uppercase tracking-wider text-zinc-500 whitespace-nowrap">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   4) TYPING FORM (or real screen recording) — replaced by GIF when available
   ============================================================ */
export function AnimationTypingForm() {
  return (
    <div className="w-full border border-zinc-200 overflow-hidden bg-white" data-testid="anim-typing-form">
      <img
        src="/landing/commande_classique.gif"
        alt="Procédé de commande sur la plateforme Hamac"
        className="w-full h-auto block"
        loading="lazy"
      />
      <div className="flex items-center justify-between px-3 py-2 border-t border-zinc-100 bg-zinc-50">
        <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Plateforme · Démo</span>
        <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-400">commande classique</span>
      </div>
    </div>
  );
}

/* ============================================================
   5) GARANTIE / REFABRICATION (or real screen recording) — replaced by GIF when available
   ============================================================ */
export function AnimationCountdown() {
  return (
    <div className="w-full border border-zinc-200 overflow-hidden bg-white" data-testid="anim-countdown">
      <img
        src="/landing/refabrication.gif"
        alt="Demande de refabrication / SAV sur la plateforme Hamac"
        className="w-full h-auto block"
        loading="lazy"
      />
      <div className="flex items-center justify-between px-3 py-2 border-t border-zinc-100 bg-zinc-50">
        <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Plateforme · Démo</span>
        <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-400">refabrication SAV</span>
      </div>
    </div>
  );
}
